import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCashflow } from '../context/CashflowContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ExpenseFormDialog from '../components/ExpenseFormDialog';
import {
  Plus,
  Pencil,
  Trash2,
  Calendar as CalendarIcon,
  CheckCircle2,
  MoreVertical,
  Search,
  Upload,
  FileText,
  Download
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import Papa from 'papaparse';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import api from '../lib/api';

export default function ExpensesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    expenses, 
    categories, 
    activeCashflow,
    createExpense, 
    updateExpense, 
    deleteExpense,
    markExpensePaid,
    toggleExpense,
    refreshExpenses,
    loading 
  } = useCashflow();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [importing, setImporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Check for action=add in URL and auto-open dialog
  useEffect(() => {
    if (searchParams.get('action') === 'add' && categories.length > 0) {
      setIsDialogOpen(true);
      // Clear the action param
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, categories, setSearchParams]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    category_id: '',
    expense_type: 'personal',
    frequency: 'monthly',
    due_date: new Date(),
    end_date: null,
    notes: '',
    gst_inclusive: false,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      category_id: categories[0]?.id || '',
      expense_type: 'personal',
      frequency: 'monthly',
      due_date: new Date(),
      end_date: null,
      notes: '',
      gst_inclusive: false,
    });
    setEditingExpense(null);
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        name: expense.name,
        amount: expense.amount.toString(),
        category_id: expense.category_id,
        expense_type: expense.expense_type,
        frequency: expense.frequency,
        due_date: parseISO(expense.due_date),
        end_date: expense.end_date ? parseISO(expense.end_date) : null,
        notes: expense.notes || '',
        gst_inclusive: expense.gst_inclusive || false,
      });
    } else {
      resetForm();
      if (categories.length > 0) {
        setFormData(prev => ({ ...prev, category_id: categories[0].id }));
      }
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against double-submit

    if (!formData.name || !formData.amount || !formData.category_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const data = {
        name: formData.name,
        amount: parseFloat(formData.amount),
        category_id: formData.category_id,
        expense_type: formData.expense_type,
        frequency: formData.frequency,
        // Send the calendar date as picked, not a UTC instant — toISOString()
        // shifted Sydney dates back a day (audit C1).
        due_date: format(formData.due_date, 'yyyy-MM-dd'),
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : '',
        notes: formData.notes,
        gst_inclusive: formData.gst_inclusive,
      };

      if (editingExpense) {
        await updateExpense(editingExpense.id, data);
        toast.success('Expense updated');
      } else {
        await createExpense(data);
        toast.success('Expense created');
      }
      
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving expense');
    } finally {
      setSubmitting(false);
    }
  };

  // CSV Import handling
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    
    try {
      const text = await file.text();
      // Proper CSV parsing (PapaParse): handles quoted fields, embedded commas,
      // empty fields and CRLF line endings — the old regex parser did not.
      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: (h) => h.trim().toLowerCase(),
        transform: (v) => (v ?? '').trim(),
      });

      const expenses = parsed.data
        .map((row) => {
          const expense = { ...row };
          // Strip thousands separators so "1,200.50" imports as 1200.50.
          if (expense.amount) expense.amount = String(expense.amount).replace(/,/g, '');
          return expense;
        })
        .filter((row) => Object.values(row).some((v) => v !== ''));

      if (expenses.length === 0) {
        toast.error('No valid expense rows found in CSV');
        return;
      }

      // Send to backend
      const response = await api.post('/expenses/import', {
        expenses,
        cashflow_id: activeCashflow.id
      });

      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} expenses`);
        if (refreshExpenses) refreshExpenses();
      }

      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
        console.error('Import errors:', response.data.errors);
      }

      setIsImportDialogOpen(false);
    } catch (error) {
      toast.error('Error importing CSV: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `name,amount,category,type,frequency,due_date,gst_inclusive,notes
"Office Rent",2500,Rent/Mortgage,business,monthly,2026-03-01,true,"Monthly office rent"
"Internet",80,Utilities,business,monthly,2026-03-05,true,""
"Gym Membership",60,Memberships,personal,monthly,2026-03-01,false,""
"Car Insurance",1200,Motor Vehicles,personal,annual,2026-06-15,true,"Annual premium"
"Groceries",150,Other,personal,weekly,2026-03-01,false,"Weekly shopping"`;

    const blob = new Blob([template], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'cashflow_expense_import_template.csv';
    link.click();
    toast.success('Template downloaded');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteExpense(id);
        toast.success('Expense deleted');
      } catch (error) {
        toast.error('Error deleting expense');
      }
    }
  };

  const handleMarkPaid = async (expense) => {
    try {
      // Local calendar date (backend stores a date, not an instant — audit C1).
      await markExpensePaid(expense.id, format(new Date(), 'yyyy-MM-dd'), expense.amount);
      toast.success('Marked as paid');
    } catch (error) {
      toast.error('Error marking as paid');
    }
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = exp.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || exp.expense_type === filterType;
    const matchesCategory = filterCategory === 'all' || exp.category_id === filterCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  // Format currency (2 decimal places so line items sum to totals visually)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading || !activeCashflow) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Expenses
          </h1>
          <p className="text-stone-500 mt-1">
            Manage your recurring bills and expenses
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Import CSV Button */}
          <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="rounded-[4px]"
                data-testid="import-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Import Expenses from CSV
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-sm text-stone-600">
                  Upload a CSV file with your expenses. The file should have columns for: name, amount, category, type, frequency, due_date, gst_inclusive, notes.
                </p>
                
                <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-stone-400 mb-3" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label 
                    htmlFor="csv-upload"
                    className="cursor-pointer"
                  >
                    <span className="text-[#021d49] font-medium hover:underline">
                      Click to upload
                    </span>
                    <span className="text-stone-500"> or drag and drop</span>
                  </label>
                  <p className="text-xs text-stone-400 mt-2">CSV files only</p>
                </div>

                {importing && (
                  <div className="flex items-center justify-center gap-2 text-[#021d49]">
                    <div className="spinner w-4 h-4" />
                    <span>Importing...</span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadTemplate}
                    className="rounded-[4px]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsImportDialogOpen(false)}
                    className="rounded-[4px]"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Expense Button */}
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
          <ExpenseFormDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            title={editingExpense ? 'Edit Expense' : 'Add New Expense'}
            submitLabel={editingExpense ? 'Update' : 'Create'}
            formData={formData}
            setFormData={setFormData}
            categories={categories}
            onSubmit={handleSubmit}
            isSubmitting={submitting}
            testIdPrefix="expense"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-[4px]"
            data-testid="expense-search-input"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 rounded-[4px]" data-testid="filter-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="business">Business</SelectItem>
            <SelectItem value="personal">Personal</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48 rounded-[4px]" data-testid="filter-category-select">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: cat.color }}
                  />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Expenses List */}
      {filteredExpenses.length === 0 ? (
        <Card className="bg-white border border-stone-200 rounded-lg">
          <CardContent className="empty-state">
            <div className="text-stone-300 mb-4">
              <CalendarIcon className="w-16 h-16" />
            </div>
            <h3 className="text-lg font-medium text-[#021d49] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No expenses yet
            </h3>
            <p className="text-stone-500 mb-4">
              Add your first expense to start tracking your cashflow
            </p>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredExpenses.map((expense) => (
            <Card 
              key={expense.id}
              className={`bg-white border border-stone-200 rounded-lg card-interactive ${!expense.is_active ? 'opacity-60' : ''}`}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-4">
                  {/* Toggle */}
                  <Switch
                    checked={expense.is_active}
                    onCheckedChange={() => toggleExpense(expense.id)}
                    className="data-[state=checked]:bg-green-600"
                    data-testid={`toggle-${expense.id}`}
                  />

                  {/* Category Color */}
                  <div 
                    className="w-3 h-3 rounded-full hidden md:block"
                    style={{ backgroundColor: categories.find(c => c.id === expense.category_id)?.color || '#78716C' }}
                  />

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-stone-900">{expense.name}</h3>
                      <Badge 
                        variant="secondary"
                        className={expense.expense_type === 'business' ? 'type-business' : 'type-personal'}
                      >
                        {expense.expense_type}
                      </Badge>
                      <Badge 
                        variant="secondary"
                        className={`freq-${expense.frequency.replace('_', '-')}`}
                      >
                        {expense.frequency.replace('_', ' ')}
                      </Badge>
                      {expense.gst_inclusive && (
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                          GST
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-500 mt-1">
                      {expense.category_name} · Due {format(parseISO(expense.due_date), 'MMM d, yyyy')}
                      {expense.gst_inclusive && expense.gst_amount > 0 && (
                        <span className="text-teal-600"> · GST: {formatCurrency(expense.gst_amount)}</span>
                      )}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="text-right">
                    <p className="text-lg font-bold font-mono text-stone-900">
                      {formatCurrency(expense.amount)}
                    </p>
                    {expense.next_due_date && (
                      <p className="text-xs text-stone-500">
                        Next: {format(parseISO(expense.next_due_date), 'MMM d')}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-[4px]" data-testid={`expense-menu-${expense.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleMarkPaid(expense)}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Mark as Paid
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleOpenDialog(expense)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
